import { useMemo } from 'react';
import { ConsigneeSellerAccountSelector } from 'src/components/Selectors/ConsigneeSellerAccountSelector';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { PosFormField } from 'src/core/POS/PosFormField';
import { useUserCanCreatePurchaseForConsignee } from 'src/hooks/useUserIsConsignorMasterUI';
import { ContentId } from 'src/utils/constants/contentId';

type PurchaseConsigneeSelectorProps = {
  /** Null means the purchase is created under the current (consignor) account */
  value: string | null | undefined;
  onChange: (consigneeSellerAccountId: string | null) => void;
  disabled?: boolean;
};

/**
 * Optional consignee account to create a purchase under (consignor master view only).
 * Renders nothing for anyone else.
 */
export const PurchaseConsigneeSelector = ({
  value,
  onChange,
  disabled,
}: PurchaseConsigneeSelectorProps) => {
  const { loginContext } = useAppContext();
  const canCreatePurchaseForConsignee = useUserCanCreatePurchaseForConsignee();

  const ownAccountId = loginContext?.user?.activeAccount?.id;
  // Empty already means "my own account", so it isn't offered as an option
  const excludedSellerAccountIds = useMemo(
    () => (ownAccountId ? [ownAccountId] : []),
    [ownAccountId]
  );

  if (!canCreatePurchaseForConsignee) {
    return null;
  }

  return (
    <PosFormField label={<Content id={ContentId.SellerAccount} />}>
      <ConsigneeSellerAccountSelector
        // Remount when cleared - the underlying Radix select otherwise keeps
        // showing the previous consignee as checked once the value goes empty
        key={value ? 'selected' : 'empty'}
        style={{ width: '100%' }}
        disabled={disabled}
        value={value ?? undefined}
        excludedSellerAccountIds={excludedSellerAccountIds}
        placeholderText={ContentId.NoneSelected}
        showClearButton
        onChange={(newValue) => {
          const consigneeSellerAccountId = newValue || null;
          if (consigneeSellerAccountId !== (value ?? null)) {
            onChange(consigneeSellerAccountId);
          }
        }}
      />
    </PosFormField>
  );
};
